import {
  chakra,
  TableBody,
  TableBodyProps,
  TableCell,
  TableCellProps,
  TableColumnHeader,
  TableColumnHeaderProps,
  TableHeader,
  TableHeaderProps,
  TableRoot,
  TableRootProps,
  TableRow,
  TableRowProps,
} from "@chakra-ui/react";
import {typedMemo} from "../../../utils/typescript";
import {TableConfig} from "../../../hooks/table";
import {forwardRef, memo} from "react";
import {Link as RouterLink, To} from "react-router-dom";

export const BaseTableRoot = typedMemo(<C extends TableConfig>({config, ...rest}: {config: C} & TableRootProps) => {
  return (
    <TableRoot
      size="spacious"
      tableLayout="fixed"
      interactive={config.getRowLink != null || config.onRowClick != null}
      // This is required to allow the contents of cells to fill cells.
      // See: https://stackoverflow.com/a/73399942/26853284
      height="fit-content"
      {...rest}
    />
  );
});

export const BaseTableHead = typedMemo(
  forwardRef<HTMLTableSectionElement, TableHeaderProps>((rest, ref) => {
    return <TableHeader ref={ref} {...rest} />;
  }),
);

export const BaseTableBody = typedMemo(
  forwardRef<HTMLTableSectionElement, TableBodyProps>((rest, ref) => {
    return <TableBody ref={ref} {...rest} />;
  }),
);

export const BaseTableRow = memo(
  forwardRef<HTMLTableRowElement, TableRowProps>((rest, ref) => {
    return <TableRow ref={ref} {...rest} />;
  }),
);

export const BaseTableCell = memo(
  forwardRef<HTMLTableCellElement, TableCellProps>((rest, ref) => {
    return <TableCell ref={ref} {...rest} />;
  }),
);

export const BaseTableCellLink = memo(
  forwardRef<HTMLTableCellElement, {to: To} & TableCellProps>(
    ({to, children, tabIndex, alignItems, justifyContent, ...rest}, ref) => {
      return (
        <TableCell ref={ref} p="0 !important" {...rest}>
          <chakra.a
            asChild
            // This class name grants the "spacious" padding to the cell contents
            // (see styles in theme.ts).
            className="cell-link"
            display="flex"
            alignItems={alignItems}
            justifyContent={justifyContent}
            h="full"
            tabIndex={tabIndex}
          >
            <RouterLink to={to}>{children}</RouterLink>
          </chakra.a>
        </TableCell>
      );
    },
  ),
);

export const BaseTableColumnHeader = memo(
  forwardRef<HTMLTableCellElement, TableColumnHeaderProps>((rest, ref) => {
    return <TableColumnHeader ref={ref} {...rest} />;
  }),
);
